import { ref, computed, watch, nextTick, onBeforeMount, onMounted, onBeforeUnmount, toValue, useSSRContext } from 'vue'
import { storeToRefs } from 'pinia'
import { useHead } from '@unhead/vue'

import { ParagraphType, EmbedType, LinkTarget } from '@xcel-next/core/enums'
import { ImageSrcSetFormat } from '@xcel-next/rendering-tools/types/constants'

import { useConfigStore } from '../stores/config'
import { usePageStore } from '../stores/page'
import EventBus from '../event-bus/EventBus'
import { useFlickitySlider } from './use-flickity-slider'
import { useUrlHelper } from './use-url-helper'
import { useImgSrcSet } from './use-img-src-set'
import { useDeviceDetection } from './use-device-detection'
import { useAdaMoltenBundle } from './use-ada-molten-bundle'
import { useArticleSliderFocusTrap } from './use-article-slider-focus-trap'
import { getSliderImageQuery, isSliderIndexInActiveWindow, resolveSliderInitialIndex } from '../utils/slider-initial-index'

import type { Ref, MaybeRefOrGetter } from 'vue'
import type { Brand } from '@xcel-next/core/enums'
import type { ComposedMetaData, ComposedParagraphTeaser } from '@xcel-next/composer-schema'
import type { ImageObject } from './use-img-src-set'
import type { SliderGalleryItem } from '../utils/paragraph-schema/paragraphs'

const SLIDER_AD_RELOAD_COOLDOWN_SECONDS = 10

type UseParagraphTypeSliderOptions = {
  flickityDiv: Ref<HTMLElement | null>,
  galleryItems: MaybeRefOrGetter<SliderGalleryItem[]>,
  brand: MaybeRefOrGetter<Brand | undefined>,
  isPreview: MaybeRefOrGetter<boolean>,
  metaData: MaybeRefOrGetter<ComposedMetaData | undefined>,
  singleSlideUrls: MaybeRefOrGetter<boolean>,
  sliderNo: MaybeRefOrGetter<number>,
  initialIndex: MaybeRefOrGetter<number>
}

export const useParagraphTypeSlider = (options: UseParagraphTypeSliderOptions) => {
  const { flickityDiv } = options

  const configStore = useConfigStore()
  const { pageData } = storeToRefs(usePageStore())
  const { getQueryParams } = useUrlHelper()
  const { createImgSrc, getImageFormat } = useImgSrcSet()
  const { deviceType } = useDeviceDetection()
  const registerSlots = ref<string[]>([])
  const { adaSlotReloadOnInteraction } = useAdaMoltenBundle({ registerSlots, deviceType })

  const galleryItems = computed(() => toValue(options.galleryItems) ?? [])
  const brand = computed(() => toValue(options.brand))
  const isPreview = computed(() => toValue(options.isPreview))
  const metaData = computed(() => toValue(options.metaData))
  const singleSlideUrls = computed(() => toValue(options.singleSlideUrls))
  const sliderNo = computed(() => toValue(options.sliderNo))
  const initialIndex = computed(() => toValue(options.initialIndex))
  const ssrContext = import.meta.env.SSR ? useSSRContext<{ url?: string }>() : undefined
  const getImageSuffix = (index: number): string => singleSlideUrls.value && index !== 0 ? `#image=${index + 1}` : ''

  // useHash defaults to true for post-mount callers (hashchange, initFlickity); the very first
  // ref value below must stay false, since the server never sees the hash and we'd otherwise hydrate-mismatch
  const resolveInitialIndex = (useHash = true): number => {
    const requestUrl = typeof window === 'undefined' ? ssrContext?.url : window.document.location.search

    return resolveSliderInitialIndex({
      galleryLength: galleryItems.value.length,
      initialIndex: initialIndex.value,
      imageHash: useHash && typeof window !== 'undefined' ? window.document.location.hash : undefined,
      imageQuery: getSliderImageQuery(requestUrl ?? ''),
      resolveUrl: singleSlideUrls.value
    })
  }

  const resolvedInitialIndex = ref(resolveInitialIndex(false))

  const {
    flickity,
    sliderControlDescription,
    sliderSettings,
    initFlickitySlider,
    triggerResize,
    checkButtonPosition,
    initTabindices,
    updateCellInertState,
    setAriaAttributes
  } = useFlickitySlider(flickityDiv)

  const { keepTabbingFocusInArticleSlider } = useArticleSliderFocusTrap(flickityDiv, { triggerResize })

  const viewMode = ref<'slider' | 'overview'>('slider')
  const viewportWidth = ref(0)
  const progressMovement = ref<'within' | 'leaveLeft' | 'leaveRight'>('within')
  const currentIndex = ref(resolvedInitialIndex.value)

  const supportedEmbeds: Record<string, boolean> = {
    twitter: true,
    facebook: true,
    instagram: true,
    pinterest: true,
    youtube: true,
    tiktok: true
  }
  const supportedEmbedsString = computed(() => Object.keys(supportedEmbeds).filter(key => supportedEmbeds[key]).join(', '))

  const reloadAdsOnInteraction = computed(() => configStore.rsConfig.adSlotUsage?.reloadAdsOnInteraction)
  const forceTeaserGroup = computed(() => sliderSettings.value?.forceTeaserGroup)
  const overviewEnabled = computed(() => sliderSettings.value?.enableOverview)

  const sliderContainerClasses = {
    'bx-content': true,
    'bx-slider': true,
    'bx-slider--article': true,
    'bx-typo--content-media': true,
    'bx-js-hide-buttons': true
  }
  const flickityContainerClasses = {
    ...sliderContainerClasses,
    'bx-slider--initializing': !isPreview.value
  }

  const setInitializing = (initializing: boolean): void => {
    // Keep Vue from replacing Flickity's imperatively managed container classes after init.
    if (!isPreview.value) flickityDiv.value?.classList.toggle('bx-slider--initializing', initializing)
  }

  const progressMarkerClasses = computed(() => ({
    'bx-js-marker-leave-left': progressMovement.value === 'leaveLeft',
    'bx-js-marker-leave-right': progressMovement.value === 'leaveRight'
  }))

  const composedTeasers = computed((): ComposedParagraphTeaser[] =>
    galleryItems.value
      .filter(item => item.paragraphType === ParagraphType.image)
      .map((item, index) => {
        const text = item.paragraphs?.[0]?.text
        const heading = (text?.match(/<h[23]>(.*?)<\/h[23]>/)?.[1] ?? '').replace(/<[^>]*>/g, '')
        const paragraph = (text?.match(/<p>(.*?)<\/p>/)?.[1] ?? '').replace(/<[^>]*>/g, '')
        return {
          imageId: item.image?.imageId,
          imageAltText: item.image?.imageAltText,
          imageCredit: item.image?.imageCredit,
          titleShort: heading,
          textShort: paragraph,
          url: `${pageData.value?.pageMeta?.url}${getImageSuffix(index)}`
        }
      })
  )

  const setProgressMarker = (prop: string, val: string): void => {
    document.body.style.setProperty(`--slider-progress-marker-${prop}`, val)
  }

  const initProgress = (index: number): void => {
    setProgressMarker('width', `${100 / galleryItems.value.length}%`)
    setProgressMarker('left', `${index * 100 / galleryItems.value.length}%`)
  }

  const updateProgress = (index: number, prevIndex: number): void => {
    progressMovement.value = index === 0 && prevIndex + 1 === galleryItems.value.length
      ? 'leaveRight'
      : index + 1 === galleryItems.value.length && prevIndex === 0
        ? 'leaveLeft'
        : 'within'
    setProgressMarker('left', `${index * 100 / galleryItems.value.length}%`)
  }

  const getLoadingValueByIndex = (index: number): 'eager' | 'lazy' =>
    // Only current image eager to reduce initial load (time to interactive)
    index === currentIndex.value ? 'eager' : 'lazy'

  const isSliderPlayerActive = (index: number): boolean =>
    isSliderIndexInActiveWindow(index, currentIndex.value, galleryItems.value.length)

  const forceTeaserGroupParagraph = (item: SliderGalleryItem) => ([{
    paragraphType: ParagraphType.teaserGroup,
    teaserGroup: {
      teaserGroupType: forceTeaserGroup.value?.type,
      teasers: [item.teaser]
    }
  }])

  const paragraphsData = (item: SliderGalleryItem) =>
    forceTeaserGroup.value?.type && item.paragraphType === ParagraphType.teaser ? forceTeaserGroupParagraph(item) : [item]

  const paragraphsMetaData = (item: SliderGalleryItem) =>
    forceTeaserGroup.value?.type && item.paragraphType === ParagraphType.teaser
      ? { ...metaData.value, teaserGroupType: forceTeaserGroup.value?.type }
      : metaData.value

  const buttonPositionLogic = (item: SliderGalleryItem) =>
    forceTeaserGroup.value?.buttonPositionLogic && item.paragraphType === ParagraphType.teaser
      ? forceTeaserGroup.value?.buttonPositionLogic
      : item.paragraphType

  // called with either the gallery item's `.image` or (pre-existing quirk) the whole gallery item -
  // only the former actually has `.layout`, hence the cast below
  const srcSetName = (image?: SliderGalleryItem['image'] | SliderGalleryItem): string => {
    const layout = (image as { layout?: { size?: string } } | undefined)?.layout
    return 'paragraph_' + (layout ? layout.size : 'l')
  }

  /**
   * Fills datalayer and sends pageview to GA
   */
  const triggerGAForSlide = (index: number): void => {
    const galleryItem = galleryItems.value[index]
    const dataLayerEvent: {
      event: string,
      slide_number: number,
      slide_type: SliderGalleryItem['paragraphType'],
      slide_embed_type?: EmbedType,
      slide_image_url?: string | null,
      slide_teaser_url?: string
    } = {
      event: 'custom_gallery_slide_pageview',
      slide_number: index + 1, // index starts at 0 - actual counting starts from 1
      slide_type: galleryItem.paragraphType
    }
    switch (galleryItem.paragraphType) {
      case ParagraphType.embed:
        dataLayerEvent.slide_embed_type = galleryItem.embed?.embedType
        break
      case ParagraphType.image:
        dataLayerEvent.slide_image_url = createImgSrc({ image: galleryItem.image as ImageObject, brand: brand.value ?? '', setName: srcSetName(galleryItem.image) })
        break
      case ParagraphType.teaser:
        dataLayerEvent.slide_teaser_url = galleryItem.teaser?.url
        break
    }
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(dataLayerEvent)
  }

  const isSupportedEmbed = (embedType?: string): boolean =>
    embedType !== undefined ? !!supportedEmbeds[embedType] : false

  const isImagePortraitFormat = (item?: SliderGalleryItem): boolean => {
    if (!item) return false
    const format = getImageFormat(item.image as ImageObject)
    return !!format && format !== 'landscape'
  }

  const getImageExternalLinkAttributes = (item: SliderGalleryItem): {
    href?: string,
    target?: NonNullable<SliderGalleryItem['image']>['target'],
    rel?: NonNullable<SliderGalleryItem['image']>['rel']
  } => {
    if (item.image?.externalUrl) {
      let url = item.image.externalUrl.replace('http://', '')
      if (!url.startsWith('https://')) {
        url = 'https://' + url
      }
      return {
        href: url,
        target: item.image.target ?? LinkTarget.blank,
        rel: item.image.rel
      }
    }
    return {}
  }

  const getFigureStyleClassByFormat = (isPortrait = true): string =>
    isPortrait ? 'bx-slider__image bx-slider__image--portrait' : 'bx-slider__image'

  const postFormatImageDescription = (html?: string): string =>
    html && html.length > 0 ? html.replace(/<\/?(p|h2|h3|br)(.|\n)*?>/gi, ' ') : ''

  const checkEmptyOrUnsizedSlide = (slideId: number): void => {
    const slider = flickityDiv.value
    if (!slider) return

    const slide = slider.querySelector(`#bx-js-slider-cell-${sliderNo.value}-${slideId}`)
    if (!slide) return

    // check for empty slides and add class to container to display placeholder
    if (slide.scrollHeight !== 0 || slide.classList.contains('bx-slider__cell--empty')) return

    slide.classList.add('bx-slider__cell--empty')
    triggerResize()
  }

  const setBlurredBackground = (index: number): void => {
    const wrapIndex = (index + 1) % galleryItems.value.length
    const item = galleryItems.value[wrapIndex]
    if (isImagePortraitFormat(item)) {
      const bgImgDiv = flickityDiv.value?.querySelector<HTMLElement>(`#bx-js-slider-cell-bg-${sliderNo.value}-${wrapIndex}`)
      if (bgImgDiv) {
        const imgSrc = createImgSrc({
          image: item.image as ImageObject,
          brand: brand.value ?? '',
          type: 'webp',
          setName: 'blur'
        })
        bgImgDiv.style.backgroundImage = 'url(' + imgSrc + ')'
      }
    }
  }

  const stopSlideVideos = (prevIndex: number): void => {
    const prevGalleryItem = galleryItems.value[prevIndex]
    if (!prevGalleryItem) return

    if (prevGalleryItem.embed?.embedType === EmbedType.youtube) {
      // select youtube iframe
      const wrapperEl = flickityDiv.value?.querySelector(`#bx-js-slider-cell-${sliderNo.value}-${prevIndex}`)
      const youtubeIframe = wrapperEl?.querySelector<HTMLIFrameElement>('.yt-iframe')
      youtubeIframe?.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*') // stop the video
    } else if (prevGalleryItem.video) {
      const wrapperEl = flickityDiv.value?.querySelector(`#bx-js-slider-cell-${sliderNo.value}-${prevIndex}`)
      const jwEl = wrapperEl?.querySelector<HTMLElement>('.jwplayer')
      if (jwEl && window.jwplayer) {
        const player = window.jwplayer(jwEl.id)
        player.stop() // stops video. if we just use player.pause(), it will trigger the video to be started in case its not
        player.pauseAd(true)
      }
    }
  }

  const changeSlideUrl = (index: number): void => {
    if (!isPreview.value && singleSlideUrls.value && window.document.location.hash !== `#image=${index + 1}` && window.document.location.search !== `?image=${index + 1}`) {
      history.pushState({}, '', `${window.document.location.href.replace(window.document.location.hash, '')}#image=${index + 1}`)
    }
  }

  const initFlickity = async (): Promise<void> => {
    resolvedInitialIndex.value = resolveInitialIndex()
    currentIndex.value = resolvedInitialIndex.value
    setInitializing(true)

    initProgress(resolvedInitialIndex.value)
    await initFlickitySlider({
      // Override default option-properties
      initialIndex: resolvedInitialIndex.value,
      lazyLoad: 1, // load images lazily, 1 image ahead
      pageDots: galleryItems.value.length < 20,
      wrapAround: true,
      freeScroll: false,
      adaptiveHeight: true,
      selectedAttraction: 0.8, // higher value speeds up slide animation
      friction: 1, // higher value reduces bounce and speeds up slide animation
      on: {
        ready: () => {
          initTabindices()
          updateCellInertState()
          setAriaAttributes()
          checkButtonPosition()
          triggerResize()
          setBlurredBackground(currentIndex.value)
          setBlurredBackground((currentIndex.value + 1) % galleryItems.value.length)
          setBlurredBackground((currentIndex.value - 1 + galleryItems.value.length) % galleryItems.value.length)
        },
        change: (index: number) => {
          const prevIndex = currentIndex.value
          currentIndex.value = index
          updateProgress(index, prevIndex)
          updateCellInertState()
          // A single requestAnimationFrame still fires BEFORE the browser paints, so anything
          // here was still blocking the slide-change paint (bad for INP). Nesting a 2nd rAF
          // guarantees the callback runs after the paint has happened.
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              // Blurred background for +/-1 (wraparound)
              setBlurredBackground(index)
              setBlurredBackground((index + 1) % galleryItems.value.length)
              setBlurredBackground((index - 1 + galleryItems.value.length) % galleryItems.value.length)

              stopSlideVideos(prevIndex)
              changeSlideUrl(index)
              checkEmptyOrUnsizedSlide(index)

              // Analytics/tracking - non-visual, safe to run after paint
              if (!isPreview.value && sliderSettings.value?.triggerGAOnSlide) {
                triggerGAForSlide(index)
              }

              // HEAVY work: ad reload, pushed onto a macrotask so it can't extend the current frame
              if (reloadAdsOnInteraction.value?.enabled) {
                const onAdReload = () => {
                  if (sliderSettings.value?.triggerAGFOnAdReload) EventBus.$emit('triggerAGF')
                }
                setTimeout(() => {
                  const reloadConfig = reloadAdsOnInteraction.value!.config
                  adaSlotReloadOnInteraction({
                    ...reloadConfig,
                    minDisplayDuration: Math.max(reloadConfig.minDisplayDuration, SLIDER_AD_RELOAD_COOLDOWN_SECONDS)
                  }, onAdReload)
                }, 0)
              }
            })
          })
        },
        settle: () => {
          triggerResize()
        }
      }
    }).finally(() => {
      setInitializing(false)
    })
  }

  const handleViewModeUrl = (mode: 'slider' | 'overview' = 'overview'): void => {
    const currentUrl = new URL(window.location.href)
    if (mode === 'overview') {
      currentUrl.searchParams.set('show', 'overview')
    } else {
      currentUrl.searchParams.delete('show')
    }
    if (singleSlideUrls.value) {
      window.history.pushState({}, '', currentUrl.href)
    }
  }

  const handleViewMode = async (): Promise<void> => {
    handleViewModeUrl(viewMode.value)
    if (viewMode.value === 'overview') {
      document.body.classList.add('bx-js-show-slider-overview')
      flickity.value?.destroy()
      flickityDiv.value?.setAttribute('style', 'height: auto;')
    } else {
      document.body.classList.remove('bx-js-show-slider-overview')
      await initFlickity()
    }
  }

  const handlePopstate = (): void => {
    viewMode.value = window.document.location.search?.includes('show=overview') ? 'overview' : 'slider'
  }

  const setViewMode = async (): Promise<void> => {
    if (overviewEnabled.value) {
      window.addEventListener('popstate', handlePopstate)

      if (getQueryParams()?.show === 'overview') {
        viewMode.value = 'overview'
        return // setting `viewMode` here will trigger the watcher and call `handleViewMode()`, so we eject here to not call it twice at the end of this method again
      }
    }

    await handleViewMode()
  }

  const handleBottomBackToGallery = (): void => {
    viewMode.value = 'slider'

    nextTick(() => {
      const yOffset = -120
      const sliderTop = flickityDiv.value?.getBoundingClientRect().top ?? 0
      const y = sliderTop + window.scrollY + yOffset
      window.scrollTo({ top: y })
    })
  }

  const handleHashChange = (): void => {
    flickity.value?.select(resolveInitialIndex())
  }

  /**
   * Used in The GUI to move to the current slide on mouseOver
   */
  // Is used by services/the-gui/src/components/preview/model-to-renderer-adapter/Slider.vue as a event-trigger
  const moveToSlide = (index: number, isInstant?: boolean): void => {
    if (!flickity.value) {
      return // has not been available yet
    }
    setBlurredBackground(index)
    setBlurredBackground(index + 1)
    setBlurredBackground(index - 1)
    // https://flickity.metafizzy.co/api.html#select
    const selectedIndex = flickity.value.selectedIndex
    if (selectedIndex !== index) {
      // isWrapping exists at runtime but is missing from the flickity type defs
      const isWrapping = (flickity.value as unknown as { isWrapping?: boolean }).isWrapping
      flickity.value.select(index, isWrapping, isInstant)
    }
    checkButtonPosition()
  }

  const getImageGalleryStructure = () => {
    const baseUrl = `${pageData.value?.pageMeta?.baseUrl ?? ''}${pageData.value?.pageMeta?.url ?? ''}`
    const imageGallery: {
      '@context': string,
      '@type': string,
      description?: string,
      url: string,
      associatedMedia: Array<{ '@type': string, contentUrl: string | null, description: string, url: string }>
    } = {
      '@context': 'http://schema.org/',
      '@type': 'ImageGallery',
      description: pageData.value?.metaData?.metaDescription,
      url: baseUrl,
      associatedMedia: []
    }
    galleryItems.value.forEach((item, index) => {
      if (item.image) {
        imageGallery.associatedMedia.push({
          '@type': 'ImageObject',
          contentUrl: createImgSrc({
            image: item.image as ImageObject,
            brand: brand.value ?? '',
            width: 980,
            setName: srcSetName(item.image)
          }),
          description: item.image?.imageDescription?.plain || item.image?.imageAltText || '',
          url: baseUrl + getImageSuffix(index)
        })
      } else if (item.teaser) {
        imageGallery.associatedMedia.push({
          '@type': 'ImageObject',
          // teaser images use a different shape (imageWidth/imageHeight/croppings) than ImageObject expects -
          // pre-existing quirk carried over from the original implementation, not fixed here.
          contentUrl: createImgSrc({
            image: item.teaser as unknown as ImageObject,
            brand: brand.value ?? '',
            width: 970,
            setName: 'bx-teaser-container--full-width',
            srcSetDefinition: { format: ImageSrcSetFormat.Landscape }
          }),
          description: item.teaser?.imageAltText || '',
          url: baseUrl + getImageSuffix(index)
        })
      }
    })
    return imageGallery
  }

  useHead(computed(() => (
    !isPreview.value
      ? { script: [{ 'data-vmid': 'structured-data-image-gallery', type: 'application/ld+json', innerHTML: JSON.stringify(getImageGalleryStructure()) }] }
      : {}
  )))

  watch(viewMode, async (newVal, oldVal) => {
    if (newVal !== oldVal) await handleViewMode()
  })

  onBeforeMount(() => {
    viewportWidth.value = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth
  })

  onMounted(async () => {
    // prevent slider wrapper collapse while slider initialize before first image is loaded
    const firstSlide = flickityDiv.value?.querySelector<HTMLElement>('.bx-slider__cell--first-slide')
    flickityDiv.value?.setAttribute('style', `min-height: ${firstSlide?.offsetHeight}px`)
    await setViewMode()

    if (singleSlideUrls.value) window.addEventListener('hashchange', handleHashChange)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('popstate', handlePopstate)
    if (singleSlideUrls.value) window.removeEventListener('hashchange', handleHashChange)
  })

  return {
    flickityDiv,
    flickity,
    viewMode,
    viewportWidth,
    currentIndex,
    resolvedInitialIndex,
    sliderControlDescription,
    flickityContainerClasses,
    sliderContainerClasses,
    progressMarkerClasses,
    supportedEmbedsString,
    overviewEnabled,
    composedTeasers,
    isSupportedEmbed,
    isImagePortraitFormat,
    getImageExternalLinkAttributes,
    getFigureStyleClassByFormat,
    srcSetName,
    postFormatImageDescription,
    getLoadingValueByIndex,
    isSliderPlayerActive,
    paragraphsData,
    paragraphsMetaData,
    buttonPositionLogic,
    handleBottomBackToGallery,
    keepTabbingFocusInArticleSlider,
    moveToSlide
  }
}
